import React from 'react';

import { DeleteIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { viewerCan } from '@vlabs/pages/auth/selectors';

import { DeleteListsForm } from '@vlabs/monokit/entities/lists';

import { useDeleteLists } from './useDeleteLists';
import { buildTestid } from '../../../shared/lib/utils';
import type { DeleteListsProps } from '../types';

interface DeleteListsIconButton extends
  DeleteListsProps,
  IconButtonProps { }

const displayName = 'DeleteListsIconButton';
const testid = buildTestid(displayName);

function DeleteListsIconButton({
  listIds,
  onDeleteLists = undefined,
  children = undefined,
  permissions = undefined,
  'data-testid': dataTestId = undefined,
  disabled = false,
  ...props
}: DeleteListsIconButton): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal();
  const can = useSelector(viewerCan);
  const deleteLists = useDeleteLists();

  const { rules = [], method = 'oneof' } = permissions ?? {};

  const hasPermission = rules.length ? can(rules, { method }) : true;
  const $disabled = !hasPermission || disabled;

  return (
    <React.Fragment>
      {modal.wrap(
        <DeleteListsForm
          listIds={listIds}
          onCancel={modal.close}
          onSubmit={async (values) => {
            await deleteLists.mutateAsync(values);
            onDeleteLists?.();
            modal.close();
          }}
        />,
      )}

      <IconButton
        disabled={$disabled}
        icon={<DeleteIcon />}
        kind="negative"
        onClick={modal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      >
        {children !== undefined ? children : t('features:delete_lists.button')}
      </IconButton>
    </React.Fragment>
  );
}

DeleteListsIconButton.displayName = displayName;

export { DeleteListsIconButton };
